import type {
  FriendLinkLandingRequest,
  PageActionEvent,
  PageActionRequest,
  PopupEventType,
  SDKContext,
  TriggerType,
} from "./types";

// ---------------------------------------------------------------------------
// Event buffer & periodic flush
// ---------------------------------------------------------------------------

/** Buffered events awaiting flush. */
let eventBuffer: PageActionEvent[] = [];

/** Interval handle for periodic flush (1s). */
let flushIntervalId: ReturnType<typeof setInterval> | null = null;

/** Reference to the SDK context (set on init). */
let ctx: SDKContext | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialise the logger module with the SDK context.
 * Starts the periodic flush timer and registers beforeunload/pagehide
 * beacon handlers.
 */
export function initLogger(context: SDKContext): void {
  ctx = context;

  // Periodic flush every 1 second (chatboost-compatible)
  flushIntervalId = setInterval(() => {
    flushEvents();
  }, 1000);

  // Ensure buffered events are sent on page unload via sendBeacon
  window.addEventListener("pagehide", handlePageHide);
  window.addEventListener("beforeunload", handleBeforeUnload);
}

/**
 * Record a popup event (show / click / close).
 */
export function recordEvent(
  type: PopupEventType,
  trigger: TriggerType,
  extra?: { area_number?: number },
): void {
  const event: PageActionEvent = {
    type,
    timestamp: Date.now(),
    trigger,
    visibility_state: document.visibilityState,
    ...extra,
  };
  eventBuffer.push(event);
}

/**
 * Flush buffered events immediately (returns a Promise for await scenarios).
 */
export async function flushEvents(): Promise<void> {
  if (!ctx || eventBuffer.length === 0) return;

  const events = eventBuffer.splice(0, eventBuffer.length);
  const payload = buildPayload(events);

  try {
    await fetch(`${ctx.logApiBase}/api/page-actions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Re-insert events if send failed so they can be retried
    eventBuffer.unshift(...events);
  }
}

/**
 * Send a LINE friend-link-landing tracking event.
 */
export async function sendFriendLinkLanding(): Promise<void> {
  if (!ctx) return;

  const payload: FriendLinkLandingRequest = {
    session_id: ctx.sessionId,
    page_view_id: ctx.pageViewId,
    popup_config_id: ctx.config.id,
    line_official_account_id: ctx.config.line_official_account_id,
    user_agent: navigator.userAgent,
    url: location.href,
    referrer: document.referrer || null,
    device_type: ctx.isMobile ? "mobile" : "pc",
    timestamp: Date.now(),
  };

  try {
    await fetch(`${ctx.logApiBase}/api/v1/friend-link-landings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Best effort — don't block the LINE redirect
  }
}

/**
 * Stop the periodic flush and clean up event listeners.
 */
export function destroyLogger(): void {
  if (flushIntervalId !== null) {
    clearInterval(flushIntervalId);
    flushIntervalId = null;
  }
  window.removeEventListener("pagehide", handlePageHide);
  window.removeEventListener("beforeunload", handleBeforeUnload);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function buildPayload(events: PageActionEvent[]): PageActionRequest {
  return {
    session_id: ctx!.sessionId,
    page_view_id: ctx!.pageViewId,
    popup_configuration_id: ctx!.config.id,
    events,
    user_agent: navigator.userAgent,
    url: location.href,
  };
}

/**
 * Use sendBeacon for reliable delivery on page hide / unload.
 */
function sendViaBeacon(): void {
  if (!ctx || eventBuffer.length === 0) return;

  const events = eventBuffer.splice(0, eventBuffer.length);
  const payload = buildPayload(events);

  const sent = navigator.sendBeacon(
    `${ctx.logApiBase}/api/page-actions`,
    JSON.stringify(payload),
  );

  if (!sent) {
    // If sendBeacon fails, put events back (unlikely to be recovered, but
    // matches chatboost behavior)
    eventBuffer.unshift(...events);
  }
}

function handlePageHide(): void {
  sendViaBeacon();
}

function handleBeforeUnload(): void {
  sendViaBeacon();
}
